import PropTypes from 'prop-types';
import { useState, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import * as Cesium from 'cesium';
import { pointCloudProfile } from '../utils/pointCloudProfile';
import { useProfile } from '../hooks/useProfile';
import TipsWindow from '../components/TipsWindow';
import { useTip } from '../hooks/useTips';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import Slider from '@mui/material/Slider';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlineOutlined';
import CreateIcon from '@mui/icons-material/Create';
import AlignHorizontalCenterIcon from '@mui/icons-material/AlignHorizontalCenter';
import AlignVerticalCenterIcon from '@mui/icons-material/AlignVerticalCenter';

import { ROW_STYLES, BUTTON_STYLES } from '../styles/viewerStyles';

// --- Profile drag helper functions (extracted for clarity) ---

/** Pick a point on a plane using ray intersection */
const pickOnPlane = (viewer, windowPosition, plane) => {
  if (!viewer || !plane || !windowPosition) return null;
  const ray = viewer.camera.getPickRay(windowPosition);
  if (!ray) return null;
  return Cesium.IntersectionTests.rayPlane(ray, plane) || null;
};

/** Check if a world point is inside a profile's bounding box */
const pointInsideBox = (worldPoint, profile) => {
  if (!worldPoint || !profile?.model) return false;
  const { center, dimensions, orientation } = profile.model;
  if (!center || !dimensions || !orientation) return false;

  const rel = Cesium.Cartesian3.subtract(worldPoint, center, new Cesium.Cartesian3());
  const R = Cesium.Matrix3.transpose(orientation, new Cesium.Matrix3());
  const local = Cesium.Matrix3.multiplyByVector(R, rel, new Cesium.Cartesian3());
  const half = new Cesium.Cartesian3(dimensions.x * 0.5, dimensions.y * 0.5, dimensions.z * 0.5);

  return Math.abs(local.x) <= half.x + 1e-3 &&
         Math.abs(local.y) <= half.y + 1e-3 &&
         Math.abs(local.z) <= half.z + 1e-3;
};

/** Pick local coordinates relative to a profile's center */
const pickLocal = (viewer, windowPosition, profile) => {
  if (!profile?.model) return null;
  const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(profile.model.center, new Cesium.Cartesian3());
  const plane = Cesium.Plane.fromPointNormal(profile.model.center, up);
  const planePt = pickOnPlane(viewer, windowPosition, plane);
  const worldPt = viewer.scene.pickPosition(windowPosition) || planePt;
  if (!worldPt) return null;

  const rel = Cesium.Cartesian3.subtract(worldPt, profile.model.center, new Cesium.Cartesian3());
  const R = Cesium.Matrix3.transpose(profile.model.orientation, new Cesium.Matrix3());
  const local = Cesium.Matrix3.multiplyByVector(R, rel, new Cesium.Cartesian3());
  return { local, plane, planePt, worldPt };
};

/** Check if cursor is near a profile endpoint for resize mode */
const nearEndpoint = (viewer, windowPosition, profile) => {
  const data = pickLocal(viewer, windowPosition, profile);
  if (!data) return { which: null, plane: null, planePt: null };

  const { local, plane, planePt } = data;
  const { dimensions, orientation, center } = profile.model;
  const halfX = dimensions.x * 0.5;
  const halfY = dimensions.y * 0.5;

  // Thresholds tuned for usability
  const yThresh = Math.max(1.0, Math.min(5.0, dimensions.y * 0.05));
  const xSlack = Math.max(1.0, halfX * 0.5);

  // Determine which real point maps to +/-Y by projecting endpoints
  const R = Cesium.Matrix3.transpose(orientation, new Cesium.Matrix3());
  const l1 = Cesium.Matrix3.multiplyByVector(R, Cesium.Cartesian3.subtract(profile.model.point1 || center, center, new Cesium.Cartesian3()), new Cesium.Cartesian3());
  const l2 = Cesium.Matrix3.multiplyByVector(R, Cesium.Cartesian3.subtract(profile.model.point2 || center, center, new Cesium.Cartesian3()), new Cesium.Cartesian3());
  const posEnd = (l1.y > l2.y) ? 'point1' : 'point2';
  const negEnd = (l1.y > l2.y) ? 'point2' : 'point1';

  if (Math.abs(local.y - halfY) <= yThresh && Math.abs(local.x) <= (halfX + xSlack)) {
    return { which: posEnd, plane, planePt };
  }
  if (Math.abs(local.y + halfY) <= yThresh && Math.abs(local.x) <= (halfX + xSlack)) {
    return { which: negEnd, plane, planePt };
  }
  return { which: null, plane: null, planePt: null };
};

/** Disable camera controls and save previous state */
const disableCameraControls = (viewer, stateRef) => {
  const c = viewer.scene.screenSpaceCameraController;
  stateRef.current = stateRef.current || {
    enableRotate: c.enableRotate,
    enableTranslate: c.enableTranslate,
    enableZoom: c.enableZoom,
    enableTilt: c.enableTilt,
    enableLook: c.enableLook,
  };
  c.enableRotate = false;
  c.enableTranslate = false;
  c.enableZoom = false;
  c.enableTilt = false;
  c.enableLook = false;
};

/** Restore camera controls from saved state */
const restoreCameraControls = (viewer, stateRef) => {
  const c = viewer.scene.screenSpaceCameraController;
  const prev = stateRef.current;
  if (prev) {
    c.enableRotate = prev.enableRotate;
    c.enableTranslate = prev.enableTranslate;
    c.enableZoom = prev.enableZoom;
    c.enableTilt = prev.enableTilt;
    c.enableLook = prev.enableLook;
  }
  stateRef.current = null;
};

function ProfilerTools({ viewer, tileset, updateProfilerHighlighting, onOpenPreview, profileLoadingId }) {
  const { t } = useTranslation();

  // Tips integration - component is only mounted when panel is open
  const { isOpen: isTipOpen, title: tipTitle, markdownBody: tipBody, close: closeTip } = useTip('profiler', true);

  const [isSelecting, setIsSelecting] = useState(false);
  const [defaultWidth, setDefaultWidth] = useState(1.0);
  const { profiles, activeProfileId, addProfile, updateProfileWidth, updateProfileOffset, updateProfilePointOffset, deleteProfile, setActive } = useProfile();
  const updateHighlightRef = useRef(updateProfilerHighlighting);
  const updateOffsetRef = useRef(updateProfileOffset);
  const updatePointRef = useRef(updateProfilePointOffset);
  useEffect(() => { updateHighlightRef.current = updateProfilerHighlighting; }, [updateProfilerHighlighting]);
  useEffect(() => { updateOffsetRef.current = updateProfileOffset; }, [updateProfileOffset]);
  useEffect(() => { updatePointRef.current = updateProfilePointOffset; }, [updateProfilePointOffset]);
  const selectHandlerRef = useRef(null);
  const dragHandlerRef = useRef(null);
  // Drag state for moving active profile
  const dragRef = useRef({ active: false, mode: null, which: null, plane: null, lastPoint: null, profileId: null });
  const activeProfileRef = useRef(null);
  const camCtrlStateRef = useRef(null);
  useEffect(() => {
    activeProfileRef.current = profiles.find(p => p.id === activeProfileId) || null;
  }, [profiles, activeProfileId]);
  const firstPointRef = useRef(null);
  const previewLineRef = useRef(null);
  const mousePosRef = useRef(null);
  // Preview window managed by parent (BaseCesiumViewer)

  const stopSelecting = useCallback(() => {
    if (selectHandlerRef.current) {
      try {
        selectHandlerRef.current.destroy();
      } catch (error) {
        console.warn('Error destroying screen space event handler:', error);
      }
      selectHandlerRef.current = null;
    }
    if (viewer && previewLineRef.current) {
      try {
        viewer.entities.remove(previewLineRef.current);
      } catch (error) {
        console.warn('Error removing preview line entity:', error);
      }
      previewLineRef.current = null;
    }
    firstPointRef.current = null;
    setIsSelecting(false);
    if (updateProfilerHighlighting) {
      try {
        updateProfilerHighlighting(false);
      } catch (error) {
        console.warn('Error updating profiler highlighting:', error);
      }
    }
  }, [viewer, updateProfilerHighlighting]);

  // Start drag on left-down inside the highlighted box; move while pressed; end on left-up
  useEffect(() => {
    if (!viewer || isSelecting) return;

    // Clean previous drag handler
    if (dragHandlerRef.current) {
      try { dragHandlerRef.current.destroy(); } catch (e) { console.warn('Failed to destroy drag handler:', e); }
      dragHandlerRef.current = null;
    }

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
    const resetDragState = () => ({ active: false, mode: null, which: null, plane: null, lastPoint: null, profileId: null });

    // LEFT_DOWN: Start drag (endpoint resize or box move)
    handler.setInputAction((down) => {
      const ap = activeProfileRef.current;
      if (!ap?.model) return;

      const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(ap.model.center, new Cesium.Cartesian3());
      const plane = Cesium.Plane.fromPointNormal(ap.model.center, up);
      const planePt = pickOnPlane(viewer, down.position, plane);
      if (!planePt) return;

      // Check if near endpoint (resize mode)
      const end = nearEndpoint(viewer, down.position, ap);
      if (end.which) {
        dragRef.current = { active: true, mode: 'endpoint', which: end.which, plane: end.plane || plane, lastPoint: end.planePt || planePt, profileId: ap.id };
        viewer.canvas.style.cursor = 'move';
        disableCameraControls(viewer, camCtrlStateRef);
        return;
      }

      // Check if inside box (drag mode)
      const depthPt = viewer.scene.pickPosition(down.position);
      const inside = (depthPt && pointInsideBox(depthPt, ap)) || pointInsideBox(planePt, ap);
      if (!inside) return;

      dragRef.current = { active: true, mode: 'box', which: null, plane, lastPoint: planePt, profileId: ap.id };
      viewer.canvas.style.cursor = 'grabbing';
      disableCameraControls(viewer, camCtrlStateRef);
    }, Cesium.ScreenSpaceEventType.LEFT_DOWN);

    // MOUSE_MOVE: Handle drag or hover feedback
    handler.setInputAction((move) => {
      const st = dragRef.current;

      if (st.active) {
        // Active drag
        const curr = pickOnPlane(viewer, move.endPosition, st.plane);
        if (!curr || !st.lastPoint) return;

        const delta = Cesium.Cartesian3.subtract(curr, st.lastPoint, new Cesium.Cartesian3());
        if (Cesium.Cartesian3.magnitudeSquared(delta) <= 0) return;

        const updated = (st.mode === 'endpoint' && st.which)
          ? updatePointRef.current?.(st.profileId, st.which, delta)
          : updateOffsetRef.current?.(st.profileId, delta);

        if (updated?.model && updateHighlightRef.current) {
          updateHighlightRef.current(true, updated.model.center, updated.model.dimensions, updated.model.orientation);
        }
        st.lastPoint = curr;
        viewer.scene.requestRender();
        return;
      }

      // Hover feedback
      const ap = activeProfileRef.current;
      if (!ap?.model) {
        viewer.canvas.style.cursor = 'default';
        return;
      }

      const up = Cesium.Ellipsoid.WGS84.geodeticSurfaceNormal(ap.model.center, new Cesium.Cartesian3());
      const plane = Cesium.Plane.fromPointNormal(ap.model.center, up);
      const probe = viewer.scene.pickPosition(move.endPosition) || pickOnPlane(viewer, move.endPosition, plane);

      if (!probe) {
        viewer.canvas.style.cursor = 'default';
        return;
      }

      const e = nearEndpoint(viewer, move.endPosition, ap);
      if (e.which) {
        viewer.canvas.style.cursor = 'move';
      } else if (pointInsideBox(probe, ap)) {
        viewer.canvas.style.cursor = 'grab';
      } else {
        viewer.canvas.style.cursor = 'default';
      }
    }, Cesium.ScreenSpaceEventType.MOUSE_MOVE);

    // LEFT_UP: End drag
    handler.setInputAction(() => {
      if (!dragRef.current.active) return;
      dragRef.current = resetDragState();
      viewer.canvas.style.cursor = 'default';
      restoreCameraControls(viewer, camCtrlStateRef);
    }, Cesium.ScreenSpaceEventType.LEFT_UP);

    dragHandlerRef.current = handler;

    return () => {
      if (dragHandlerRef.current) {
        try { dragHandlerRef.current.destroy(); } catch (e) { console.warn('Cleanup: failed to destroy drag handler:', e); }
        dragHandlerRef.current = null;
      }
      dragRef.current = resetDragState();
      try{
        viewer.canvas.style.cursor = 'default';
      }catch{
        console.warn("could not reset the cursor style");
      }
    };
  }, [viewer, isSelecting]);

  const handleMouseMove = useCallback((movement) => {
    mousePosRef.current = movement.endPosition;
    if (!firstPointRef.current || !updateProfilerHighlighting) return;

    try {
      const secondPoint = viewer.scene.pickPosition(movement.endPosition);
      if (secondPoint) {
        const temporaryProfile = new pointCloudProfile(firstPointRef.current, secondPoint, defaultWidth);
        updateProfilerHighlighting(true, temporaryProfile.center, temporaryProfile.dimensions, temporaryProfile.orientation, true);
      }
      viewer.scene.requestRender();
    } catch (error) {
      console.warn('Error during mouse move handling:', error);
    }
  }, [viewer, updateProfilerHighlighting, defaultWidth]);

  const handleLeftClick = useCallback((click) => {
    const clickPosition = viewer.scene.pickPosition(click.position);
    if (!clickPosition) return;
    
    try {
      if (!firstPointRef.current) {
        firstPointRef.current = clickPosition;
        if (updateProfilerHighlighting) {
          const singlePointProfile = new pointCloudProfile(clickPosition, clickPosition, defaultWidth);
          updateProfilerHighlighting(true, singlePointProfile.center, singlePointProfile.dimensions, singlePointProfile.orientation, true);
        }
      } else {
        const startPoint = firstPointRef.current;
        const endPoint = clickPosition;
        const profileId = `profile_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
        const profileModel = new pointCloudProfile(startPoint, endPoint, defaultWidth);
        const newProfile = { id: profileId, model: profileModel, width: defaultWidth, visible: true, createdAt: Date.now() };
        
        addProfile(newProfile);
        stopSelecting();
        
        if (updateProfilerHighlighting) {
          updateProfilerHighlighting(true, profileModel.center, profileModel.dimensions, profileModel.orientation, false);
        }
        
        firstPointRef.current = null;
      }
      viewer.scene.requestRender();
    } catch (error) {
      console.warn('Error during click handling:', error);
      firstPointRef.current = null;
    }
  }, [viewer, updateProfilerHighlighting, defaultWidth, addProfile]);

  const startSelecting = useCallback(() => {
    if (!viewer) return;
    
    try {
      if (selectHandlerRef.current) {
        selectHandlerRef.current.destroy();
        selectHandlerRef.current = null;
      }
      
      const eventHandler = new Cesium.ScreenSpaceEventHandler(viewer.canvas);
      eventHandler.setInputAction(handleMouseMove, Cesium.ScreenSpaceEventType.MOUSE_MOVE);
      eventHandler.setInputAction(handleLeftClick, Cesium.ScreenSpaceEventType.LEFT_CLICK);

      selectHandlerRef.current = eventHandler;
      setIsSelecting(true);
    } catch (error) {
      console.warn('Error starting selection mode:', error);
    }
  }, [viewer, handleMouseMove, handleLeftClick]);

  const toggleSelecting = useCallback(() => {
    if (isSelecting) stopSelecting(); else startSelecting();
  }, [isSelecting, startSelecting, stopSelecting]);

  const createHorizontalSection = useCallback(() => {
    if (!tileset?.boundingSphere) return;
    const { center, radius } = tileset.boundingSphere;
    const carto = Cesium.Cartographic.fromCartesian(center);
    const delta = radius / Cesium.Ellipsoid.WGS84.maximumRadius;
    const westPoint = Cesium.Cartesian3.fromRadians(carto.longitude - delta, carto.latitude, carto.height);
    const eastPoint = Cesium.Cartesian3.fromRadians(carto.longitude + delta, carto.latitude, carto.height);
    const profileModel = new pointCloudProfile(westPoint, eastPoint, defaultWidth);
    const profileId = `profile_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    addProfile({ id: profileId, model: profileModel, width: defaultWidth, visible: true, createdAt: Date.now() });
    if (updateProfilerHighlighting) {
      updateProfilerHighlighting(true, profileModel.center, profileModel.dimensions, profileModel.orientation, false);
    }
  }, [tileset, defaultWidth, addProfile, updateProfilerHighlighting]);

  const createVerticalSection = useCallback(() => {
    if (!tileset?.boundingSphere) return;
    const { center, radius } = tileset.boundingSphere;
    const carto = Cesium.Cartographic.fromCartesian(center);
    const delta = radius / Cesium.Ellipsoid.WGS84.maximumRadius;
    const southPoint = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude - delta, carto.height);
    const northPoint = Cesium.Cartesian3.fromRadians(carto.longitude, carto.latitude + delta, carto.height);
    const profileModel = new pointCloudProfile(southPoint, northPoint, defaultWidth);
    const profileId = `profile_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
    addProfile({ id: profileId, model: profileModel, width: defaultWidth, visible: true, createdAt: Date.now() });
    if (updateProfilerHighlighting) {
      updateProfilerHighlighting(true, profileModel.center, profileModel.dimensions, profileModel.orientation, false);
    }
  }, [tileset, defaultWidth, addProfile, updateProfilerHighlighting]);

  useEffect(() => {
    return () => {
      stopSelecting();
    };
  }, [stopSelecting]);

  useEffect(() => {
    if (isSelecting && firstPointRef.current) return;

    try {
      if (updateProfilerHighlighting) {
        if (!activeProfileId) {
          updateProfilerHighlighting(false);
        } else {
          const activeProfile = profiles.find(profile => profile.id === activeProfileId);
          if (activeProfile && activeProfile.model) {
            updateProfilerHighlighting(true, activeProfile.model.center, activeProfile.model.dimensions, activeProfile.model.orientation);
          }
        }
      }
    } catch (error) {
      console.warn('Error updating active profile highlighting:', error);
    }
  }, [profiles, activeProfileId, updateProfilerHighlighting, isSelecting]);

  return (
    <>
    <TipsWindow
      isOpen={isTipOpen}
      title={tipTitle}
      markdownBody={tipBody}
      onClose={({ doNotShowAgain }) => closeTip(doNotShowAgain)}
    />
    <Box>
      {/* Controls row */}
      <Box sx={ROW_STYLES.buttonRow}>
        <ToggleButton
          value="horizontal"
          selected={false}
          onChange={createHorizontalSection}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          disabled={!tileset}
          title={t('HorizontalSection')}
        >
          <AlignHorizontalCenterIcon />
        </ToggleButton>
        <ToggleButton
          value="vertical"
          selected={false}
          onChange={createVerticalSection}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          disabled={!tileset}
          title={t('VerticalSection')}
        >
          <AlignVerticalCenterIcon />
        </ToggleButton>
        <ToggleButton
          value="profiler"
          selected={isSelecting}
          onChange={toggleSelecting}
          size="small"
          sx={BUTTON_STYLES.iconButton}
          title={t('CreateProfile')}
        >
          <CreateIcon />
        </ToggleButton>


       {isSelecting && (
        <Box sx={{ px: 1.5,pt:0.25, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 1}}>
          <Typography variant="caption" sx={{ color: '#fff' }}>
            {t('SelectTwoPointsToCreateProfile') || 'Select 2 points to create a profile'}
          </Typography>
        </Box>
      )} 

      </Box>

      {profiles.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="subtitle2" sx={{ color: '#fff', fontSize: '0.75rem', mb: 0.5 }}>
            {t('Profiles') || 'Profiles'} ({profiles.length})
          </Typography>
          <Box sx={{
            maxHeight: '400px',
            overflowY: 'auto',
            pr: 1,
            '&::-webkit-scrollbar': { width: '4px' },
            '&::-webkit-scrollbar-track': { background: 'rgba(255,255,255,0.1)' },
            '&::-webkit-scrollbar-thumb': { background: 'rgba(255,255,255,0.3)', borderRadius: '2px' }
          }}>
            {profiles.map((profile) => (
              <Box
                key={profile.id}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 0.5,
                  my: 0.5,
                  bgcolor: activeProfileId === profile.id ? 'rgba(255, 255, 255, 0.3)' : 'rgba(255,255,255,0.05)',
                  borderRadius: 1,
                  border: activeProfileId === profile.id ? '2px solid rgba(255, 255, 255, 0.8)' : '1px solid rgba(255,255,255,0.1)',
                  cursor: 'pointer',
                  '&:hover': {
                    bgcolor: activeProfileId === profile.id ? 'rgba(255, 255, 255, 0.4)' : 'rgba(255,255,255,0.1)',
                  }
                }}
                onClick={(event) => {
                  if (event.target.closest('button')) return;
                  if (activeProfileId !== profile.id) {
                    setActive(profile.id);
                    try {
                      viewer.scene.requestRender();
                    } catch (error) {
                      console.warn('Error requesting render after profile selection:', error);
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                  <Typography variant="caption" 
                  sx={{ 
                    pl:1,
                    color: activeProfileId === profile.id ? '#000' : '#fff', 
                    minWidth: 68, 
                    fontSize: '0.7rem',
                    fontWeight: activeProfileId === profile.id ? 'bold' : 'normal' 
                  }}>
                    {profile.createdAt ? new Date(profile.createdAt).toLocaleTimeString() : ''}
                  </Typography>
                  <Typography variant="caption" 
                  sx={{ color: activeProfileId === profile.id ? '#000' : '#fff', 
                  minWidth: 40, 
                  fontSize: '0.7rem',
                  fontWeight: activeProfileId === profile.id ? 'bold' : 'normal' 
                  }}>
                    {profile.width.toFixed(1)} m
                  </Typography>
                  <Slider
                    value={profile.width}
                    min={0.5}
                    max={5}
                    step={0.1}
                    onChange={(_, newWidth) => {
                      try {
                        const updatedProfile = updateProfileWidth(profile.id, newWidth);
                        if (activeProfileId === profile.id && updateProfilerHighlighting && updatedProfile?.model) {
                          updateProfilerHighlighting(true, updatedProfile.model.center, updatedProfile.model.dimensions, updatedProfile.model.orientation);
                        }
                      } catch (error) {
                        console.warn('Error updating profile width:', error);
                      }
                    }}
                    sx={{
                      flex: 1,
                      mr:1,
                      color: '#fff',
                      '& .MuiSlider-thumb': { width: 10, height: 10 },
                      '& .MuiSlider-rail': { height: 2 },
                      '& .MuiSlider-track': { height: 2 },
                    }}
                  />
                </Box>
                <IconButton
                  size="small"
                  onClick={(event) => { event.stopPropagation(); onOpenPreview && onOpenPreview(profile); }}
                  disabled={profileLoadingId === profile.id}
                  sx={{ color: '#fff', p: 0.5, opacity: profileLoadingId === profile.id ? 0.7 : 1 }}
                  title={t('View') || 'View'}
                >
                  {profileLoadingId === profile.id ? (
                    <CircularProgress size={16} thickness={5} sx={{ color: '#fff' }} />
                  ) : (
                    profile.visible !== false ? <VisibilityIcon sx={{ fontSize: '16px' }} /> : <VisibilityOffIcon sx={{ fontSize: '16px' }} />
                  )}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => { deleteProfile(profile.id); }}
                  sx={{ color: '#fff', p: 0.5, '&:hover': { bgcolor: 'rgba(255,100,100,0.2)' } }}
                  title={t('Delete') || 'Delete'}
                >
                  <DeleteOutlineIcon sx={{ fontSize: '16px' }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
    </>
  );
}

ProfilerTools.propTypes = {
  viewer: PropTypes.object,
  tileset: PropTypes.object,
  updateProfilerHighlighting: PropTypes.func,
  onOpenPreview: PropTypes.func,
  profileLoadingId: PropTypes.string,
};

export default ProfilerTools;
